import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input"; 
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Mail, CalendarPlus, ListTodo, StickyNote, Timer, Search, Upload,
  Link, Share2, Languages, Mic, Video, Settings,
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { WheelMenuItem } from '@/components/consultant/WheelMenu'; // Import WheelMenuItem type

// Define structure for available actions (can be derived from WheelMenuItem if action prop is ignored)
interface AvailableAction {
  id: string;
  label: string;
  icon: React.ReactNode;
}

// Define the structure for the saved settings
export interface WheelMenuSettingsData {
  hotkey: string; // Format: "g", "ctrl+g", "alt+shift+k" etc.
  selectedItemIds: string[];
  holdMode: boolean; // New hold mode setting
}

interface WheelMenuSettingsProps {
  isVisible: boolean;
  onClose: () => void;
  currentSettings: WheelMenuSettingsData; // Pass current settings in
  onSave: (newSettings: WheelMenuSettingsData) => void; // Callback when saving
  // Add allWheelActions prop from App.tsx
  allWheelActions: Record<string, Omit<WheelMenuItem, 'action' | 'id'> & { id: string, label: string, icon: React.ReactNode }>; 
}

// Helper to format hotkey string for display
const formatHotkeyDisplay = (hotkey: string): string => {
  if (!hotkey) return '';
  return hotkey
    .split('+')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1)) // Capitalize each part
    .join(' + ');
};

const WheelMenuSettings: React.FC<WheelMenuSettingsProps> = ({ 
  isVisible, onClose, currentSettings, onSave, allWheelActions // Destructure new prop
}) => {
  const [hotkey, setHotkey] = useState(currentSettings.hotkey);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(currentSettings.selectedItemIds)
  );
  const [holdMode, setHoldMode] = useState(currentSettings.holdMode);
  const [listeningHotkey, setListeningHotkey] = useState(false);

  // Convert allWheelActions (object) to an array of AvailableAction for rendering
  const displayableActions: AvailableAction[] = Object.values(allWheelActions).map(action => ({
    id: action.id,
    label: action.label,
    icon: action.icon
  }));

  // Ref to track the latest selected IDs for saving immediately
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
      selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // Ref to track the latest hotkey for saving immediately
  const hotkeyRef = useRef(hotkey);
  useEffect(() => {
    hotkeyRef.current = hotkey;
  }, [hotkey]);

  // Ref to track the latest hold mode for saving immediately
  const holdModeRef = useRef(holdMode);
  useEffect(() => {
    holdModeRef.current = holdMode;
  }, [holdMode]);

  // Update local state if currentSettings prop changes
  useEffect(() => {
    // Only update if the incoming settings differ from the current state
    // to avoid overwriting unsaved changes if the dialog is reopened quickly
    // (Though immediate save makes this less critical)
    if (currentSettings.hotkey !== hotkeyRef.current) {
        setHotkey(currentSettings.hotkey);
    }
    if (currentSettings.holdMode !== holdModeRef.current) {
        setHoldMode(currentSettings.holdMode);
    }
    const currentSet = new Set(currentSettings.selectedItemIds);
    if (currentSet.size !== selectedIdsRef.current.size || 
        ![...currentSet].every(id => selectedIdsRef.current.has(id))) {
        setSelectedIds(currentSet);
    }
  }, [currentSettings]);

  const handleItemToggle = (itemId: string) => {
    const newSet = new Set(selectedIdsRef.current); // Use ref for current state
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    setSelectedIds(newSet); // Update state
    // Save immediately
    onSave({ 
      hotkey: hotkeyRef.current, 
      selectedItemIds: Array.from(newSet),
      holdMode: holdModeRef.current
    });
  };

  const handleHoldModeToggle = (checked: boolean) => {
    setHoldMode(checked);
    // Save immediately
    onSave({
      hotkey: hotkeyRef.current,
      selectedItemIds: Array.from(selectedIdsRef.current),
      holdMode: checked
    });
  };

  // Updated hotkey handler
  const handleHotkeyInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault(); 
    const key = e.key.toLowerCase();
    
    // Ignore standalone modifier key presses
    if (['control', 'alt', 'shift', 'meta'].includes(key)) {
        return; 
    }

    const parts: string[] = [];
    if (e.ctrlKey) parts.push('ctrl');
    if (e.altKey) parts.push('alt');
    if (e.shiftKey) parts.push('shift');
    if (e.metaKey) parts.push('meta'); // Cmd/Win key

    // Add the actual key, mapping space if necessary
    const finalKey = key === ' ' ? 'space' : key;
    // Basic filter: only add if it's a single char, number, space, or function key (like F1-F12)
    if (/^[a-z0-9]$/.test(finalKey) || finalKey === 'space' || /^f\d{1,2}$/.test(finalKey)) {
        parts.push(finalKey);
    } else {
        // Didn't capture a valid final key, maybe just modifiers were held?
        console.warn('Invalid key combination for hotkey:', key);
        return; // Don't set an incomplete hotkey
    }

    const newHotkey = parts.join('+');

    if (newHotkey !== hotkeyRef.current) { // Only save if hotkey actually changed
        setHotkey(newHotkey); // Update state
        onSave({ 
          hotkey: newHotkey, 
          selectedItemIds: Array.from(selectedIdsRef.current),
          holdMode: holdModeRef.current
        });
    }
    
    setListeningHotkey(false);
    e.currentTarget.blur(); 
  }, [onSave]);

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && onClose()}>
      {/* Allow dialog content to grow wider if needed, max 70% height */}
      <DialogContent className="sm:max-w-xl md:max-w-2xl lg:max-w-3xl max-h-[70dvh] overflow-hidden flex flex-col"> 
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Wheel Menu Settings</DialogTitle>
          <DialogDescription>
            Configure the trigger hotkey and select actions for the wheel.
          </DialogDescription>
        </DialogHeader>
        
        {/* Hotkey Input Section - Compact */}
        <div className="pt-2 pb-2 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Label htmlFor="hotkey" className="whitespace-nowrap text-sm">
              Trigger Key
            </Label>
            <Input 
              id="hotkey" 
              value={listeningHotkey ? 'Press key...' : formatHotkeyDisplay(hotkey)} 
              onClick={() => setListeningHotkey(true)}
              onBlur={() => setListeningHotkey(false)}
              onKeyDown={listeningHotkey ? handleHotkeyInputKeyDown : undefined} 
              readOnly
              className={cn(
                "w-32 cursor-pointer text-center font-semibold text-sm h-8",
                listeningHotkey && "ring-2 ring-primary ring-offset-1 ring-offset-background animate-pulse"
              )}
              placeholder="Click to set"
            />
            {/* Hold Mode Toggle - Inline */}
            <div className="flex items-center gap-2 ml-auto">
              <Label htmlFor="hold-mode" className="text-sm">
                Hold Mode
              </Label>
              <Switch
                id="hold-mode"
                checked={holdMode}
                onCheckedChange={handleHoldModeToggle}
              />
            </div>
          </div>
        </div>

        {/* Item Selection - Scrollable Area */}
        <div className="flex-1 overflow-y-auto min-h-0 pt-2">
            <h4 className="text-xs font-medium mb-2 text-center text-muted-foreground">Select Actions (Max 8 Recommended)</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5 p-1">
            {displayableActions.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                <Button
                    key={item.id}
                    variant={isSelected ? "secondary" : "outline"} 
                    className={cn(
                        "h-auto flex flex-col items-center justify-center p-1.5 gap-0.5 text-center transition-all min-h-[52px]",
                        isSelected && "border-primary border-2 shadow-sm bg-primary/10", 
                        !isSelected && "hover:shadow-sm hover:bg-accent hover:text-accent-foreground border"
                    )}
                    onClick={() => handleItemToggle(item.id)}
                >
                    {React.cloneElement(item.icon as React.ReactElement, { className: "h-4 w-4" } as any)}
                    <span className="text-[10px] font-medium leading-tight line-clamp-2">{item.label}</span>
                </Button>
                );
            })}
            </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WheelMenuSettings; 